import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import {
  BlockHeader,
  FORM_PRIMARY_BUTTON_SX,
  FORM_SECONDARY_BUTTON_SX,
  FORM_SWITCH_SX,
  PUBLICATION_BLOCK_SX,
} from '../../Map/components/DockPanel/Tabs/formBlockUi';
import { colors, typography } from '../../styles/theme';
import {
  canSubmitCadGeoref,
  cadGeorefSubmitHint,
  isCadRefLotId,
} from '../utils/cadGeoref';
import {
  cadGeorefStickerRotationDeg,
  cadGeorefStickerScaleMul,
} from '../utils/cadGeorefSticker';
import {
  CAD_CANVAS_IMAGE_CONTENT_TYPES,
  CAD_CANVAS_IMAGE_TRANSFORM_RESET,
  isCadCanvasImageTransformDirty,
  normalizeCadCanvasImageTransform,
} from '../utils/cadCanvasImage';
import {
  showroomFieldSx,
  showroomFormFooterSx,
  showroomScrollSx,
} from '../Showroom.styles';

const HINT_SX = {
  color: colors.textSecondary,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  lineHeight: 1.45,
};

const STEP_BLOCK_SX = {
  ...PUBLICATION_BLOCK_SX,
  p: 1.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
};

const STEP_ICON_SX = { fontSize: 16, color: colors.textSecondary };

const PICK_BANNER_SX = {
  border: `1px solid ${colors.primary}`,
  bgcolor: 'rgba(72,146,145,0.12)',
  borderRadius: '8px',
  px: 1.5,
  py: 1.25,
  color: colors.textPanelTitle,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  lineHeight: 1.4,
};

const SLIDER_SX = {
  color: colors.primary,
  py: 0.5,
  '& .MuiSlider-thumb': {
    width: 14,
    height: 14,
  },
  '& .MuiSlider-rail': {
    opacity: 0.35,
  },
};

function sliderNumber(value) {
  return Number(Array.isArray(value) ? value[0] : value);
}

const IMAGE_ACCEPT = [...CAD_CANVAS_IMAGE_CONTENT_TYPES, '.png', '.jpg', '.jpeg', '.webp'].join(',');

const IMAGE_PREVIEW_SX = {
  width: '100%',
  maxHeight: 132,
  objectFit: 'contain',
  borderRadius: '8px',
  border: `1px solid ${colors.border}`,
  bgcolor: colors.backgroundDark,
};

const LOCATE_ICON_BUTTON_SX = {
  color: colors.textPanelTitle,
  p: 0.75,
  '&:hover': {
    color: colors.primary,
    bgcolor: 'transparent',
  },
  '&.Mui-disabled': {
    color: colors.textSecondary,
    opacity: 0.4,
  },
};

const PENDING_BANNER_SX = {
  border: `1px dashed ${colors.border}`,
  borderRadius: '8px',
  px: 1.25,
  py: 0.75,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const UNDO_BUTTON_SX = {
  color: colors.primary,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  minWidth: 0,
  px: 0.5,
  flexShrink: 0,
  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
};

const FORM_DANGER_BUTTON_SX = {
  ...FORM_SECONDARY_BUTTON_SX,
  color: colors.error,
  border: `1px solid ${colors.error}`,
  '&:hover': {
    color: colors.error,
    borderColor: colors.error,
    bgcolor: 'transparent',
    boxShadow: 'none',
  },
};

/** Cambio anotado que todavía no viajó: se ve qué va a pasar y se puede volver atrás. */
function PendingChange({ label, onUndo, disabled }) {
  return (
    <Box sx={PENDING_BANNER_SX}>
      <Typography sx={{ ...HINT_SX, color: colors.textPanelTitle, flex: 1 }}>
        {label}
      </Typography>
      {onUndo ? (
        <Button
          size="small"
          variant="text"
          onClick={onUndo}
          disabled={disabled}
          sx={UNDO_BUTTON_SX}
        >
          Deshacer
        </Button>
      ) : null}
    </Box>
  );
}

function SectionEditToggle({ open, onToggle, disabled, label }) {
  return (
    <Tooltip title={open ? 'Listo' : `Editar ${label}`} placement="top" arrow>
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <IconButton
          size="small"
          onClick={onToggle}
          disabled={disabled}
          aria-label={open ? `Listo ${label}` : `Editar ${label}`}
          aria-pressed={open}
          sx={LOCATE_ICON_BUTTON_SX}
        >
          {open
            ? <CheckIcon sx={{ fontSize: 18 }} />
            : <EditOutlinedIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>
    </Tooltip>
  );
}

function lotMotherSummary({
  clearFromMapPending,
  lotOk,
  refLotId,
  hasSticker,
}) {
  if (clearFromMapPending) return 'Sale del mapa al guardar';
  if (lotOk) return `Lote seleccionado · ${String(refLotId).trim()}`;
  if (hasSticker) return 'Loteo en el mapa · sin lote madre';
  return 'Sin lote madre';
}

function imageBaseSummary({
  imageRemoved,
  imageUploading,
  imageError,
  hasImage,
  showPlanImage,
}) {
  if (imageRemoved) return 'Se quita al guardar';
  if (imageUploading) return 'Subiendo la imagen…';
  if (imageError) return 'No se pudo subir. Elegí el archivo de nuevo.';
  if (!hasImage) return 'Sin imagen';
  return showPlanImage ? 'Imagen del plano · Visible' : 'Imagen del plano · Oculta';
}

/**
 * Owner (`can_publish`): referenciar el plano sobre el mapa.
 * Tres secciones con la misma lógica visual: resumen + Editar / Listo.
 * Paso 1: parcela madre. Paso 2: arrastrar / escala / rotación.
 * Paso 3: imagen base ilustrada debajo de los lotes (archivo propio).
 *
 * Nada de esto impacta hasta **Guardar**: elegir imagen, quitarla, el
 * switch y sacar el loteo del mapa son borrador. Cancelar deja la lista
 * como estaba.
 */
export default function CadGeorefPanel({
  isEditing = false,
  waitingForLot = false,
  refLotId = '',
  onLocateLot,
  onClearMotherLot,
  onClearFromMap,
  onUndoClearFromMap,
  clearFromMapPending = false,
  sticker = null,
  canSnapToLot = false,
  hasLotSnapTarget = false,
  onSnapToLot,
  onScaleMulChange,
  onRotationDegChange,
  saving = false,
  dirty = false,
  onCancel,
  onSubmit,
  image = null,
  imageUploading = false,
  imageRemoved = false,
  imageDraft = false,
  imageError = false,
  imageTransform = null,
  adjustingImage = false,
  onImageUpload,
  onImageDelete,
  onImageUndoDelete,
  onImageTransformChange,
  onAdjustingImageChange,
  onPickOnMap,
  showPlanImage = true,
  onShowPlanImageChange,
}) {
  const fileInputRef = useRef(null);
  const [editingLot, setEditingLot] = useState(false);
  const [editingLoteo, setEditingLoteo] = useState(false);
  const [editingImage, setEditingImage] = useState(false);

  const hasSticker = Boolean(sticker);
  const hasImage = Boolean(image?.url);
  const lotOk = isCadRefLotId(refLotId);
  const scaleMul = cadGeorefStickerScaleMul(sticker);
  const rotationDeg = cadGeorefStickerRotationDeg(sticker);

  const closeAllSections = () => {
    setEditingLot(false);
    setEditingLoteo(false);
    setEditingImage(false);
    onAdjustingImageChange?.(false);
  };

  const openLotSection = () => {
    setEditingLot(true);
    setEditingLoteo(false);
    setEditingImage(false);
    onAdjustingImageChange?.(false);
  };

  const openLoteoSection = () => {
    setEditingLot(false);
    setEditingLoteo(true);
    setEditingImage(false);
    onAdjustingImageChange?.(false);
  };

  const openImageSection = () => {
    setEditingLot(false);
    setEditingLoteo(false);
    setEditingImage(true);
    onAdjustingImageChange?.(hasImage);
  };

  useEffect(() => {
    if (waitingForLot && !hasSticker) openLotSection();
    // Solo al pedir click en el mapa: no pelear con el toggle del usuario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingForLot, hasSticker]);

  useEffect(() => {
    if (!adjustingImage) return;
    setEditingImage(true);
    setEditingLot(false);
    setEditingLoteo(false);
  }, [adjustingImage]);

  useEffect(() => {
    if (!image?.url) setEditingImage(false);
  }, [image]);

  const canSubmit = !saving && dirty && !imageError && (
    (hasSticker && canSubmitCadGeoref({ refLotId, sticker }))
    || hasImage
    || clearFromMapPending
    || imageRemoved
  );
  const submitHint = (() => {
    if (saving || canSubmit) return null;
    if (imageError) return 'La imagen no se subió. Elegí el archivo de nuevo.';
    if (!dirty && (hasSticker || hasImage)) return 'No hay cambios para guardar.';
    return cadGeorefSubmitHint({ refLotId, sticker, hasImage });
  })();
  const snapDisabled = saving || !canSnapToLot || !onSnapToLot;
  const snapHint = !saving && snapDisabled
    ? (hasLotSnapTarget
      ? 'Ya está ajustado al lote madre'
      : 'Elegí un lote madre en el mapa')
    : 'Volver al encaje del lote madre';

  const imageBusy = imageUploading;
  const imagePose = normalizeCadCanvasImageTransform(imageTransform);
  const imageDirty = isCadCanvasImageTransformDirty(imagePose);
  const imageScaleDisabled = saving || imageBusy || !onImageTransformChange;
  const commitImagePose = (patch) => {
    onImageTransformChange?.({ ...imagePose, ...patch });
  };
  const commitUniformImageScale = (value) => {
    const scale = sliderNumber(value) / 100;
    commitImagePose({ scale, scaleX: scale, scaleY: scale });
  };

  const lotSummary = lotMotherSummary({
    clearFromMapPending,
    lotOk,
    refLotId,
    hasSticker,
  });
  const loteoSummary = adjustingImage && !editingLoteo
    ? 'Estás ajustando la imagen base'
    : `Tamaño ${Math.round(scaleMul * 100)}% · Rotación ${Math.round(rotationDeg)}°`;
  const imageSummary = imageBaseSummary({
    imageRemoved,
    imageUploading,
    imageError,
    hasImage,
    showPlanImage,
  });

  const handleImageFile = (event) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (file) onImageUpload?.(file);
  };

  const imageStepNumber = hasSticker ? '3' : '2';

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ ...showroomScrollSx, py: 1.5, gap: 2 }}>
        <Typography sx={HINT_SX}>
          {isEditing
            ? 'El loteo en el mapa y la imagen base se configuran por separado.'
            : 'El loteo puede quedar en el lienzo, sobre el mapa, sobre una imagen base, o las tres. Nada es obligatorio.'}
        </Typography>

        <Box sx={STEP_BLOCK_SX}>
          <BlockHeader
            icon={<AddLocationAltOutlinedIcon sx={STEP_ICON_SX} />}
            title="1. Lote madre"
            subtitle={editingLot
              ? (isEditing
                ? 'Elegí otro lote madre o sacá el loteo del mapa.'
                : 'Opcional. Elegí un lote en el mapa para apoyar el plano.')
              : lotSummary}
            action={(
              <SectionEditToggle
                open={editingLot}
                onToggle={() => (editingLot ? closeAllSections() : openLotSection())}
                disabled={saving}
                label="lote madre"
              />
            )}
          />
          {clearFromMapPending ? (
            <PendingChange
              label="El loteo sale del mapa al guardar."
              onUndo={onUndoClearFromMap}
              disabled={saving}
            />
          ) : null}
          {editingLot ? (
            <>
              {waitingForLot && !hasSticker ? (
                <Typography sx={PICK_BANNER_SX} role="status">
                  Hacé click en el lote madre.
                </Typography>
              ) : null}
              {!hasSticker && onPickOnMap ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onPickOnMap}
                  disabled={saving}
                  sx={{ ...FORM_SECONDARY_BUTTON_SX, width: '100%' }}
                >
                  Elegir lote en el mapa
                </Button>
              ) : null}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Lote elegido"
                  placeholder="Se completa al hacer click en el mapa"
                  value={refLotId}
                  disabled
                  autoComplete="off"
                  sx={{ ...showroomFieldSx, flex: 1 }}
                  inputProps={{
                    spellCheck: false,
                    autoComplete: 'off',
                    readOnly: true,
                  }}
                />
                <Tooltip title="Encuadrar lote madre" placement="top" arrow>
                  <Box component="span" sx={{ display: 'inline-flex', pb: 0.25 }}>
                    <IconButton
                      size="small"
                      onClick={() => onLocateLot?.(refLotId.trim())}
                      disabled={!lotOk || saving || !onLocateLot}
                      aria-label="Encuadrar lote madre"
                      sx={LOCATE_ICON_BUTTON_SX}
                    >
                      <FitScreenIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Box>
                </Tooltip>
              </Box>
              {!clearFromMapPending && (lotOk || hasSticker || (isEditing && onClearFromMap)) ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {lotOk || hasSticker ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={onClearMotherLot}
                      disabled={saving || !onClearMotherLot}
                      sx={{ ...FORM_SECONDARY_BUTTON_SX, flex: 1 }}
                    >
                      Elegir otro lote
                    </Button>
                  ) : null}
                  {isEditing && onClearFromMap ? (
                    <Tooltip title="La imagen base no se toca." placement="top" arrow>
                      <Box component="span" sx={{ display: 'flex', flex: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={onClearFromMap}
                          disabled={saving}
                          sx={{ ...FORM_DANGER_BUTTON_SX, width: '100%' }}
                        >
                          Quitar del mapa
                        </Button>
                      </Box>
                    </Tooltip>
                  ) : null}
                </Box>
              ) : null}
            </>
          ) : null}
        </Box>

        {hasSticker ? (
          <Box sx={STEP_BLOCK_SX}>
            <BlockHeader
              icon={<TuneOutlinedIcon sx={STEP_ICON_SX} />}
              title="2. Ajuste del loteo"
              subtitle={editingLoteo
                ? 'Arrastrá el loteo en el mapa, o usá tamaño y rotación.'
                : loteoSummary}
              action={(
                <SectionEditToggle
                  open={editingLoteo}
                  onToggle={() => (editingLoteo ? closeAllSections() : openLoteoSection())}
                  disabled={saving || adjustingImage}
                  label="ajuste del loteo"
                />
              )}
            />
            {editingLoteo ? (
              <>
                <Typography sx={HINT_SX}>
                  Tamaño {Math.round(scaleMul * 100)}%
                </Typography>
                <Slider
                  min={25}
                  max={400}
                  step={1}
                  value={Math.round(scaleMul * 100)}
                  disabled={saving}
                  onChange={(_, value) => onScaleMulChange?.(sliderNumber(value) / 100)}
                  onChangeCommitted={(_, value) => onScaleMulChange?.(sliderNumber(value) / 100)}
                  aria-label="Tamaño del loteo"
                  sx={SLIDER_SX}
                />
                <Typography sx={HINT_SX}>
                  Rotación {Math.round(rotationDeg)}°
                </Typography>
                <Slider
                  min={-180}
                  max={180}
                  step={1}
                  value={Math.round(rotationDeg)}
                  disabled={saving}
                  onChange={(_, value) => onRotationDegChange?.(sliderNumber(value))}
                  onChangeCommitted={(_, value) => onRotationDegChange?.(sliderNumber(value))}
                  aria-label="Rotación del loteo"
                  sx={SLIDER_SX}
                />
                <Tooltip title={snapHint} placement="top" arrow>
                  <Box component="span" sx={{ display: 'block', width: '100%' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={onSnapToLot}
                      disabled={snapDisabled}
                      sx={{ ...FORM_SECONDARY_BUTTON_SX, width: '100%' }}
                    >
                      Ajustar a lote madre
                    </Button>
                  </Box>
                </Tooltip>
              </>
            ) : null}
          </Box>
        ) : null}

        <Box sx={STEP_BLOCK_SX}>
          <BlockHeader
            icon={<ImageOutlinedIcon sx={STEP_ICON_SX} />}
            title={`${imageStepNumber}. Imagen base`}
            subtitle={editingImage
              ? (hasImage
                ? 'Arrastrá la imagen o usá tamaño, ancho, alto y rotación.'
                : 'Foto o dibujo debajo de los lotes. PNG, JPEG o WebP. Opcional.')
              : imageSummary}
            action={(
              <SectionEditToggle
                open={editingImage}
                onToggle={() => (editingImage ? closeAllSections() : openImageSection())}
                disabled={saving || imageBusy}
                label="imagen base"
              />
            )}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            hidden
            onChange={handleImageFile}
          />
          {imageRemoved ? (
            <PendingChange
              label="La imagen se saca de debajo de los lotes."
              onUndo={onImageUndoDelete}
              disabled={saving}
            />
          ) : null}
          {imageDraft && !imageUploading && !imageError ? (
            <PendingChange label="La imagen se vincula a la lista al guardar." />
          ) : null}
          {editingImage ? (
            <>
              {hasImage ? (
                <Box component="img" src={image.url} alt="" sx={IMAGE_PREVIEW_SX} />
              ) : (
                <Typography sx={HINT_SX}>
                  PNG, JPEG o WebP. Los lotes quedan arriba y se siguen pudiendo abrir.
                </Typography>
              )}
              {hasImage && onShowPlanImageChange ? (
                <FormControlLabel
                  sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
                  control={(
                    <Switch
                      size="small"
                      checked={showPlanImage}
                      disabled={saving || imageBusy}
                      onChange={(_event, checked) => onShowPlanImageChange(checked)}
                      sx={{ ...FORM_SWITCH_SX, mt: 0.25 }}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography sx={{ ...HINT_SX, color: colors.textPanelTitle, fontWeight: 600 }}>
                        Mostrar imagen del plano
                      </Typography>
                      <Typography sx={HINT_SX}>
                        Si está apagada, se ven las calles y los espacios pintados.
                      </Typography>
                    </Box>
                  )}
                />
              ) : null}
              {hasImage ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving || imageBusy || !onImageUpload}
                    startIcon={imageUploading
                      ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                      : null}
                    sx={{ ...FORM_SECONDARY_BUTTON_SX, flex: 1 }}
                  >
                    Cambiar
                  </Button>
                  <Tooltip title="El loteo en el mapa no se toca." placement="top" arrow>
                    <Box component="span" sx={{ display: 'flex', flex: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={onImageDelete}
                        disabled={saving || imageBusy || !onImageDelete}
                        sx={{ ...FORM_DANGER_BUTTON_SX, width: '100%' }}
                      >
                        Quitar
                      </Button>
                    </Box>
                  </Tooltip>
                </Box>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving || imageBusy || !onImageUpload}
                  startIcon={imageUploading
                    ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                    : null}
                  sx={{ ...FORM_SECONDARY_BUTTON_SX, width: '100%' }}
                >
                  Subir imagen
                </Button>
              )}

              {hasImage ? (
                <>
                  <Typography sx={HINT_SX}>
                    Arrastrá la imagen. Tamaño mueve los dos ejes; ancho y alto, cada uno.
                  </Typography>
                  <Typography sx={HINT_SX}>
                    Tamaño {Math.round(imagePose.scale * 100)}%
                  </Typography>
                  <Slider
                    min={25}
                    max={400}
                    step={1}
                    value={Math.round(imagePose.scale * 100)}
                    disabled={imageScaleDisabled}
                    onChange={(_, value) => commitUniformImageScale(value)}
                    onChangeCommitted={(_, value) => commitUniformImageScale(value)}
                    aria-label="Tamaño de la imagen base"
                    sx={SLIDER_SX}
                  />
                  <Typography sx={HINT_SX}>
                    Ancho {Math.round(imagePose.scaleX * 100)}%
                  </Typography>
                  <Slider
                    min={25}
                    max={400}
                    step={1}
                    value={Math.round(imagePose.scaleX * 100)}
                    disabled={imageScaleDisabled}
                    onChange={(_, value) => commitImagePose({ scaleX: sliderNumber(value) / 100 })}
                    onChangeCommitted={(_, value) => commitImagePose({ scaleX: sliderNumber(value) / 100 })}
                    aria-label="Ancho de la imagen base"
                    sx={SLIDER_SX}
                  />
                  <Typography sx={HINT_SX}>
                    Alto {Math.round(imagePose.scaleY * 100)}%
                  </Typography>
                  <Slider
                    min={25}
                    max={400}
                    step={1}
                    value={Math.round(imagePose.scaleY * 100)}
                    disabled={imageScaleDisabled}
                    onChange={(_, value) => commitImagePose({ scaleY: sliderNumber(value) / 100 })}
                    onChangeCommitted={(_, value) => commitImagePose({ scaleY: sliderNumber(value) / 100 })}
                    aria-label="Alto de la imagen base"
                    sx={SLIDER_SX}
                  />
                  <Typography sx={HINT_SX}>
                    Rotación {Math.round((imagePose.rotationRad * 180) / Math.PI)}°
                  </Typography>
                  <Slider
                    min={-180}
                    max={180}
                    step={1}
                    value={Math.round((imagePose.rotationRad * 180) / Math.PI)}
                    disabled={imageScaleDisabled}
                    onChange={(_, value) => commitImagePose({
                      rotationRad: (sliderNumber(value) * Math.PI) / 180,
                    })}
                    onChangeCommitted={(_, value) => commitImagePose({
                      rotationRad: (sliderNumber(value) * Math.PI) / 180,
                    })}
                    aria-label="Rotación de la imagen base"
                    sx={SLIDER_SX}
                  />
                  {imageDirty ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onImageTransformChange?.(CAD_CANVAS_IMAGE_TRANSFORM_RESET)}
                      disabled={saving || imageBusy || !onImageTransformChange}
                      sx={{ ...FORM_SECONDARY_BUTTON_SX, width: '100%' }}
                    >
                      Restablecer encaje
                    </Button>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ ...showroomFormFooterSx, flexDirection: 'column', alignItems: 'stretch' }}>
        {submitHint ? (
          <Typography sx={{ ...HINT_SX, mb: 0.5 }}>{submitHint}</Typography>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={saving}
            sx={{ ...FORM_SECONDARY_BUTTON_SX, flex: 1 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!canSubmit}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
            sx={{ ...FORM_PRIMARY_BUTTON_SX, flex: 1 }}
          >
            Guardar
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
